import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, forkJoin } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { GroupsBackendService } from '@core/providers/backend/groups-backend.service';
import { ContextService } from '@core/providers/context/context.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { AccFolderQUrlsService } from '@features/account/settings/acc-folder/acc-folder-qr-urls/acc-folder-q-urls.service';
import {
  IGroupCategory,
  IGroupChild,
} from '@features/account/settings/acc-folder/acc-folder-qr-urls/group-qr.interface';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { GroupsService } from '@shared/components/groups/groups.service';
import { CONSTANTS } from '@shared/constants';
import { IGroup } from '@shared/interfaces/groups/group.interface';
import { ContextObject } from '@shared/interfaces/login-object';
import { matchesSearch, toSearchQuery } from '@shared/utils/search-text';

export const activateGroupDialogConfig: MatDialogConfig = {
  width: '600px',
  maxWidth: '96vw',
  disableClose: false,
  panelClass: 'activate-groups-dialog-panel',
};

const DEACTIVATE_GROUPS_KEY = 'ACCOUNT.FOLDER.QR_URLS.DIALOGS.DEACTIVATE_GROUPS_CONFIRMATION';
const ACTIVATED_KEY = 'ACCOUNT.FOLDER.QR_URLS.DIALOGS.SUCCESS.GROUPS_QR_ACTIVATED';
const UPDATED_KEY = 'ACCOUNT.FOLDER.QR_URLS.DIALOGS.SUCCESS.GROUPS_QR_UPDATED';

/** One category's groups, narrowed to what the search left. */
interface GroupSection {
  readonly uuid: string;
  readonly name: string;
  readonly groups: readonly IGroupChild[];
}

/**
 * Picks which group QR codes are live.
 *
 * Every group is on screen at once, sectioned by category and searchable, rather than behind a
 * category picker that had to be chosen before anything appeared. The category is still what
 * decides which endpoint activates a group, so a selection spanning categories saves as one call
 * per category — and only for the categories whose selection actually changed.
 */
@Component({
  selector: 'app-activate-group-dialog',
  templateUrl: './activate-group-dialog.component.html',
  styleUrls: ['./activate-group-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    FormsModule,
    MatButton,
    MatCheckbox,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    TranslatePipe,
  ],
})
export class ActivateGroupDialogComponent implements OnInit {
  private readonly mdDialogRef = inject<MatDialogRef<ActivateGroupDialogComponent>>(MatDialogRef);
  private readonly notificationService = inject(NotificationService);
  private readonly groupsBackendService = inject(GroupsBackendService);
  private readonly groupsService = inject(GroupsService);
  private readonly accFolderQUrlsService = inject(AccFolderQUrlsService);
  private readonly contextService = inject(ContextService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly query = signal('');
  protected readonly saving = signal(false);
  protected readonly unitNamePlural = this.contextService.getAccountChildrenNameTranslation(false);

  private readonly categories = signal<readonly IGroupCategory[]>([]);
  private readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  /** What was live when the dialog opened — the baseline a save is a diff against. */
  private readonly activeIds = signal<ReadonlySet<string>>(new Set());
  private accountUuid = '';

  protected readonly sections = computed<GroupSection[]>(() => {
    const query = toSearchQuery(this.query());

    return this.categories()
      .map(category => ({
        uuid: category.uuid,
        name: category.name,
        groups: query
          ? category.groups.filter(group => matchesSearch(group.name, query))
          : category.groups,
      }))
      .filter(section => section.groups.length > 0);
  });

  protected readonly visibleGroups = computed(() =>
    this.sections().flatMap(section => section.groups)
  );

  protected readonly totalCount = computed(() =>
    this.categories().reduce((total, category) => total + category.groups.length, 0)
  );

  protected readonly selectedCount = computed(() => this.selectedIds().size);

  protected readonly hasQuery = computed(() => this.query().trim().length > 0);

  protected readonly allVisibleSelected = computed(() => {
    const selected = this.selectedIds();
    const visible = this.visibleGroups();

    return visible.length > 0 && visible.every(group => selected.has(group.uuid));
  });

  /** Nothing to save until the selection differs from what is already live. */
  protected readonly hasChanges = computed(() => this.changedCategories().length > 0);

  /**
   * How many live URLs a save would switch off, counted over the categories the save itself walks:
   * a live group absent from them is invisible to `activateCategory$`, so it cannot be counted.
   */
  private readonly deactivatingCount = computed(() => {
    const selected = this.selectedIds();
    const active = this.activeIds();

    return this.categories()
      .flatMap(category => category.groups)
      .filter(group => active.has(group.uuid) && !selected.has(group.uuid)).length;
  });

  ngOnInit(): void {
    this.getGroups$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(categories => this.categories.set(categories));

    this.accFolderQUrlsService
      .getGroupsQrUrls$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(groupQrs => {
        const active = new Set(groupQrs.filter(qr => !qr.isAccountFixedUrl).map(qr => qr.uuid));
        this.activeIds.set(active);
        this.selectedIds.set(new Set(active));
      });

    this.getContext$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((context: ContextObject) => {
        if (context.uuid !== undefined) {
          this.accountUuid = context.uuid;
        }
      });
  }

  protected isSelected(group: IGroupChild): boolean {
    return this.selectedIds().has(group.uuid);
  }

  protected toggle(group: IGroupChild): void {
    this.selectedIds.update(selected => {
      const next = new Set(selected);
      if (!next.delete(group.uuid)) {
        next.add(group.uuid);
      }

      return next;
    });
  }

  protected toggleAllVisible(): void {
    const visible = this.visibleGroups();
    const selectAll = !this.allVisibleSelected();

    this.selectedIds.update(selected => {
      const next = new Set(selected);
      for (const group of visible) {
        if (selectAll) {
          next.add(group.uuid);
        } else {
          next.delete(group.uuid);
        }
      }

      return next;
    });
  }

  protected hide(): void {
    this.mdDialogRef.close();
  }

  /**
   * Unticking a live group is the same destructive action as the list's own Deactivate, so it
   * carries the same warning — with a count, because a diff can switch several off at once.
   */
  protected save(): void {
    const changed = this.changedCategories();
    if (changed.length === 0) {
      return;
    }

    const deactivating = this.deactivatingCount();
    if (deactivating === 0) {
      this.commit(changed);

      return;
    }

    this.notificationService
      .displayConfirmationDialogWithTranslationKey(DEACTIVATE_GROUPS_KEY, {
        count: deactivating,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.commit(changed);
        }
      });
  }

  private commit(changed: IGroupCategory[]): void {
    const deactivating = this.deactivatingCount();

    this.saving.set(true);
    forkJoin(changed.map(category => this.activateCategory$(category)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.confirmWithKey(deactivating > 0 ? UPDATED_KEY : ACTIVATED_KEY);
          this.mdDialogRef.close(true);
        },
        error: () => this.saving.set(false),
      });
  }

  /** Only the categories whose live set the user actually altered. */
  private changedCategories(): IGroupCategory[] {
    const selected = this.selectedIds();
    const active = this.activeIds();

    return this.categories().filter(category =>
      category.groups.some(group => selected.has(group.uuid) !== active.has(group.uuid))
    );
  }

  private activateCategory$(category: IGroupCategory): Observable<unknown> {
    const selected = this.selectedIds();

    if (category.uuid && category.uuid !== CONSTANTS.GROUPS.NO_CATEGORY_UUID) {
      const activeChildren = category.groups
        .filter(group => selected.has(group.uuid))
        .map(group => group.uuid);

      return this.accFolderQUrlsService.setCategoryActiveChildren$(category.uuid, activeChildren);
    }

    // Groups outside a category have only a toggling endpoint, so sending one whose state already
    // matches would turn it back off again.
    const active = this.activeIds();
    const changed = category.groups
      .filter(group => selected.has(group.uuid) !== active.has(group.uuid))
      .map(group => group.uuid);

    return this.accFolderQUrlsService.toggleGroupsFixedUrl$(this.accountUuid, changed);
  }

  private getContext$(): Observable<ContextObject> {
    return this.contextService
      .getContext$()
      .pipe(filter((context: ContextObject) => context.type === CONSTANTS.CONTEXT.ACCOUNT));
  }

  private getGroups$(): Observable<IGroupCategory[]> {
    return this.groupsBackendService.getGroups$().pipe(
      tap((groups: IGroup[]) => this.groupsService.setGroups(groups)),
      switchMap(() => this.groupsService.getGroupsWithCategories$()),
      map(groups =>
        groups.map(group => ({
          uuid: group.uuid,
          name: group.name,
          groups: (group.venueGroups ?? []).map(subGroup => ({
            uuid: subGroup.uuid,
            name: subGroup.name,
            // setGroups only computes venuesCount for categories, so a child group's count has
            // to come from the venues it carries.
            venuesCount: subGroup.venues?.length ?? subGroup.venuesCount ?? 0,
          })),
        }))
      )
    );
  }
}
